<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
    cardSize: {
        type: String,
        default: 'large',
    },
})

const { t, locale } = useI18n()

// 只有大圖模式顯示左右切換按鈕與可拖曳的 slide bar；medium（總覽）僅顯示進度
const showControls = computed(() => props.cardSize === 'large')

// 影像與清單都放在 backend/media，由 api_server 的 StaticFiles 掛在 /media 提供；
// 部署機的前端是靜態檔（/usr/share/nginx/html），故網站根目錄的 /media 沒有東西，
// 要走 nginx 反代 API 的前綴（/api/media）才拿得到。本機開發可用 env 覆寫成 /media。
// 清單由 sync_images.sh 每小時同步後產生，是靜態檔，不需要後端新增端點或重啟。
const API_BASE = process.env.VUE_APP_API_BASE_URL || 'https://weather.ecusr.ncu.edu.tw/api'
const MEDIA_BASE = process.env.VUE_APP_MEDIA_BASE_URL || `${API_BASE}/media`
const INDEX_URL = `${MEDIA_BASE}/meteor_index.json`
// 事件（流星雨）清單與各事件的影像清單，同樣由 sync_images.sh 產生
const EVENTS_URL = `${MEDIA_BASE}/meteor_events.json`
const EVENT_INDEX_BASE = `${MEDIA_BASE}/meteor_events`

// 上限：流星雨當天可能上百張，只取最新 MAX_IMAGES 張（清單已依時間新→舊排序）
// 搭配下方 window 式延遲載入，實際同時只下載約 3 張，初始流量固定 ~0.43MB 與張數脫鉤，
// 因此上限可放寬到 200；唯一取捨是 slide bar 拖曳精準度會下降（自動播放/前後鍵不受影響）
const MAX_IMAGES = 200

// 尚未發生的事件在極大期前這段期間就先亮出來，兼作預告
const UPCOMING_DAYS = 30
// 事件切換在大圖與手機都要有：手機那張卡是 90% 寬的主畫面而非縮圖，
// 只有總覽的 medium 縮圖真的放不下
const SELECTOR_SIZES = ['large', 'mobile']
// 事件 id 會被組進網址，限制成安全字元
const SAFE_ID = /^[A-Za-z0-9_-]+$/

// HDE 與 HDN 在清單中已合併並依觀測時間排序，因此兩個觀測點會交錯出現
const latestImages = ref([])
const currentIndex = ref(0)

// 事件相關狀態。activeEventId 為 null 代表看的是「最近的流星」
const events = ref([])
const activeEventId = ref(null)
const eventImages = ref({})

const toImage = (img) => ({
    id: img.path,
    url: `${MEDIA_BASE}/${img.path}`,
    camera: img.camera,
    observeTime: img.observeTime,
})

// 目前正在播的清單：最近的流星，或某個事件的影像
const images = computed(() =>
    activeEventId.value === null
        ? latestImages.value
        : eventImages.value[activeEventId.value] || [],
)

const fetchImages = async () => {
    try {
        // no-store：清單每小時更新，不能讓瀏覽器拿到快取的舊版
        const response = await fetch(INDEX_URL, { cache: 'no-store' })
        if (!response.ok) return
        const data = await response.json()
        const next = data.slice(0, MAX_IMAGES).map(toImage)
        // 清單沒變就不動，避免定時重抓打斷使用者正在看的那張
        if (
            next.length === latestImages.value.length &&
            next.every((img, i) => img.id === latestImages.value[i].id)
        ) {
            return
        }
        latestImages.value = next
        // 使用者正在看某個事件時不要把他拉回第一張
        if (activeEventId.value === null) currentIndex.value = 0
    } catch (e) {
        console.warn('fetchMeteorImages failed:', e)
    }
}

// 選中的事件可能在重整後消失（上游刪掉，或過了日期區間又是 0 張而被 visibleEvents 濾掉），
// 這時沒有任何 <option> 對得上 selectValue，瀏覽器會把 selectedIndex 設成 -1，
// 琥珀色膠囊就變成一顆沒有文字的空膠囊，因此退回「最近的流星」
const dropMissingActiveEvent = () => {
    const id = activeEventId.value
    if (id !== null && !visibleEvents.value.some((ev) => ev.id === id)) {
        activeEventId.value = null
        currentIndex.value = 0
    }
}

// 事件清單尚未部署時會拿到 404，靜默略過即可，輪播本身照常運作
const fetchEvents = async () => {
    try {
        const response = await fetch(EVENTS_URL, { cache: 'no-store' })
        if (!response.ok) return
        const data = await response.json()
        if (!Array.isArray(data)) return
        const next = data.filter((ev) => ev && SAFE_ID.test(ev.id || ''))

        // 正在看的事件張數變了表示有新影像進來，清掉快取重抓
        const active = activeEventId.value
        if (active) {
            const before = events.value.find((ev) => ev.id === active)
            const after = next.find((ev) => ev.id === active)
            if (before && after && before.count !== after.count) {
                const cached = eventImages.value[active]
                delete eventImages.value[active]
                events.value = next
                // 重抓失敗就把舊清單放回去，否則相簿會空掉而且不會自己復原
                if (!(await selectEvent(active)) && cached) eventImages.value[active] = cached
                dropMissingActiveEvent()
                return
            }
        }
        events.value = next
        dropMissingActiveEvent()
    } catch (e) {
        console.warn('fetchMeteorEvents failed:', e)
    }
}

const parseDay = (iso) => Date.parse(`${iso}T00:00:00`)

// 顯示規則：已經有影像的事件一律顯示；還沒有影像的只在極大期前 UPCOMING_DAYS 天內
// 顯示成灰色預告。已過期又 0 張的事件自動消失，不需要有人回頭維護清單。
const visibleEvents = computed(() => {
    const now = Date.now()
    const horizon = now + UPCOMING_DAYS * 86400000
    return events.value
        .filter((ev) => {
            if (ev.count > 0) return true
            const start = parseDay(ev.start)
            return Number.isFinite(start) && start > now && start <= horizon
        })
        .slice()
        .sort((a, b) => (a.start < b.start ? 1 : -1))
})

const showSelector = computed(
    () => SELECTOR_SIZES.includes(props.cardSize) && visibleEvents.value.length > 0,
)

// <option> 的 value 只能是字串，用空字串代表「最近的流星」
const selectValue = computed(() => activeEventId.value ?? '')

// 疊在影像上的資訊只跟「當下這張」有關，因此不放進 v-for，
// 否則下拉選單與資訊面板要跟著每個 slide 各自重複渲染一次
const currentImage = computed(() => images.value[currentIndex.value] || null)

const eventName = (ev) =>
    locale.value.startsWith('en') && ev.nameEn ? ev.nameEn : ev.name

const shortDate = (iso) => {
    const parts = String(iso).split('-')
    return parts.length === 3 ? `${Number(parts[1])}/${Number(parts[2])}` : iso
}

// 有影像就標張數，還沒發生就標起始日
const chipLabel = (ev) =>
    ev.count > 0
        ? `${eventName(ev)} · ${ev.count}`
        : `${eventName(ev)} · ${t('events.meteor-upcoming', { date: shortDate(ev.start) })}`

// 連續切換時只認最後一次：較早的 fetch 回來時若已經不是最新請求就放棄套用，
// 否則會蓋掉使用者剛選的相簿
let selectSeq = 0

// 回傳是否真的切換成功。失敗時 activeEventId 不變，呼叫端要負責把 <select> 拉回原值
async function selectEvent(id) {
    const seq = ++selectSeq
    if (id !== null && !eventImages.value[id]) {
        try {
            const response = await fetch(`${EVENT_INDEX_BASE}/${id}.json`, { cache: 'no-store' })
            if (!response.ok) return false
            const data = await response.json()
            eventImages.value[id] = (Array.isArray(data) ? data : []).map(toImage)
        } catch (e) {
            console.warn('fetchMeteorEventImages failed:', e)
            return false
        }
    }
    // 回傳 true 讓過期的呼叫不要去還原 <select>，那會蓋掉較新的選擇
    if (seq !== selectSeq) return true
    activeEventId.value = id
    currentIndex.value = 0
    resetAutoplay()
    return true
}

async function onSelect(e) {
    const raw = e.target.value
    const ok = await selectEvent(raw === '' ? null : raw)
    // 失敗時繫結值沒有變動，Vue 不會把 DOM 拉回來，只能手動還原
    if (!ok) e.target.value = selectValue.value
    // @focus 已經停掉輪播，切換失敗時沒有人會呼叫 resetAutoplay，
    // 而焦點仍在 select 上時 blur 不一定會來，輪播就會永遠停住，所以這裡無條件恢復
    resumeAutoplay()
}

// 只載入目前與前後各一張，避免一次 eager load 全部圖片
const PRELOAD_RADIUS = 1
const shouldLoad = (index) => {
    const n = images.value.length
    if (n === 0) return false
    // 計算環狀距離（支援首尾相接的輪播）
    const diff = Math.abs(index - currentIndex.value)
    const circular = Math.min(diff, n - diff)
    return circular <= PRELOAD_RADIUS
}
let autoplayTimer = null
// selectEvent / onMounted 都是 await 完才開計時器，元件在 fetch 途中被卸載時
// onUnmounted 早就跑完了，這裡再 setInterval 就沒人清得掉，會一直抓著已卸載的元件作用域。
// medium 那張卡約每 70 秒就被輪播任務重掛一次，這個洩漏會持續累積
let disposed = false

const startAutoplay = () => {
    if (disposed) return
    autoplayTimer = setInterval(nextImage, 5000)
}

const resetAutoplay = () => {
    if (autoplayTimer) clearInterval(autoplayTimer)
    startAutoplay()
}

// select 取得焦點就暫停，否則使用者還在挑，背後的影像已經自己換過兩張
const pauseAutoplay = () => {
    if (autoplayTimer) clearInterval(autoplayTimer)
    autoplayTimer = null
}

const resumeAutoplay = () => {
    if (!autoplayTimer) startAutoplay()
}

function nextImage() {
    if (images.value.length === 0) return
    currentIndex.value = (currentIndex.value + 1) % images.value.length
}

const prevImage = () => {
    if (images.value.length === 0) return
    currentIndex.value = (currentIndex.value - 1 + images.value.length) % images.value.length
}

// 手動操作後重置自動播放計時，避免立即又跳下一張
const goPrev = () => {
    prevImage()
    resetAutoplay()
}

const goNext = () => {
    nextImage()
    resetAutoplay()
}

const onSeek = (e) => {
    currentIndex.value = Number(e.target.value)
    resetAutoplay()
}

// slide bar 已播進度的填色
const barStyle = computed(() => {
    const n = images.value.length
    const pct = n > 1 ? (currentIndex.value / (n - 1)) * 100 : 0
    return {
        background: `linear-gradient(to right, #fff 0%, #fff ${pct}%, rgba(255, 255, 255, 0.3) ${pct}%, rgba(255, 255, 255, 0.3) 100%)`,
    }
})

// cronjob 每小時同步新影像，長時間開著的頁面需定時重抓清單才看得到
const REFRESH_INTERVAL = 10 * 60 * 1000
let refreshTimer = null

const refresh = async () => {
    await fetchImages()
    await fetchEvents()
}

onMounted(async () => {
    await refresh()
    startAutoplay()
    refreshTimer = setInterval(refresh, REFRESH_INTERVAL)
})

onUnmounted(() => {
    // 要先立旗標再清計時器：清完之後才回來的 await 流程會呼叫 startAutoplay，
    // 旗標沒先立起來就會補開一個永遠清不掉的 interval
    disposed = true
    if (autoplayTimer) clearInterval(autoplayTimer)
    if (refreshTimer) clearInterval(refreshTimer)
})
</script>

<template>
    <div class="carousel-container">
        <div class="carousel-wrapper">
            <div
                v-for="(img, index) in images"
                :key="img.id"
                v-show="currentIndex === index"
                class="slide-item"
            >
                <!-- 只有 window 內（目前±1）的 slide 才掛 src，隱藏的鄰居會先下載好，
                     切換時即時顯示；window 外不載入，流量與總張數脫鉤 -->
                <img v-if="shouldLoad(index)" :src="img.url" class="main-image" decoding="async" />
                <div v-else class="main-image img-placeholder"></div>
            </div>

            <div class="overlay-top" :class="{ mobile: cardSize === 'mobile' }">
                <!-- 事件切換：大圖與手機都有。用原生 select 而非自刻面板，手機才拿得到
                     OS 選單，展開時也不會被 .carousel-container 的 overflow: hidden 裁掉 -->
                <select
                    v-if="showSelector"
                    class="event-select"
                    :class="{ active: activeEventId !== null }"
                    :aria-label="$t('events.meteor-select')"
                    :value="selectValue"
                    @change="onSelect"
                    @focus="pauseAutoplay"
                    @blur="resumeAutoplay"
                    @click.stop
                    @mousedown.stop
                >
                    <option value="">{{ $t('events.meteor-latest') }}</option>
                    <option
                        v-for="ev in visibleEvents"
                        :key="ev.id"
                        :value="ev.id"
                        :disabled="ev.count === 0"
                    >
                        {{ chipLabel(ev) }}
                    </option>
                </select>

                <div v-if="currentImage" class="info-panel" :class="{ compact: !showControls }">
                    <div class="info-tag camera-tag">📷 {{ currentImage.camera }}</div>
                    <div class="info-tag time-tag">🕒 {{ currentImage.observeTime }}</div>
                </div>
            </div>

            <template v-if="showControls">
                <button @click.stop="goPrev" class="nav-btn prev-btn">❮</button>
                <button @click.stop="goNext" class="nav-btn next-btn">❯</button>
            </template>

            <div class="slide-bar-wrapper" :class="{ compact: !showControls }">
                <input
                    class="slide-bar"
                    type="range"
                    min="0"
                    :max="images.length - 1"
                    :value="currentIndex"
                    :style="barStyle"
                    :disabled="!showControls"
                    @input.stop="onSeek"
                    @click.stop
                    @mousedown.stop
                />
                <span v-if="showControls" class="counter">
                    {{ currentIndex + 1 }} / {{ images.length }}
                </span>
            </div>
        </div>
    </div>
</template>

<style scoped>
.carousel-container {
    width: 100%;
    height: 100%;
    background: #000;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
}

.carousel-wrapper,
.slide-item {
    width: 100%;
    height: 100%;
}

.main-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

/* window 外尚未載入的 slide：顯示深色底，避免版面塌陷 */
.img-placeholder {
    background: #0b1220;
}

/* 右側留白是給大圖模式右上角那顆關閉鈕讓位，避免下拉選單或資訊面板跑到它底下 */
.overlay-top {
    position: absolute;
    top: 12px;
    left: 12px;
    right: 56px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    z-index: 3;
}

/* 手機沒有大圖右上角那顆關閉鈕（WeatherCard.vue 的 v-if="cardSize === 'large'"），
   不需要替它讓出 56px */
.overlay-top.mobile {
    right: 12px;
}

.overlay-top.mobile .event-select {
    font-size: 10px;
    padding: 2px 20px 2px 8px;
    background-position: right 7px center;
}

.info-panel {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.info-tag {
    background: rgba(0, 0, 0, 0.6);
    color: white;
    padding: 3px 8px;
    border-radius: 5px;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.03em;
    backdrop-filter: blur(4px);
    width: fit-content;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.info-panel.compact .info-tag {
    font-size: 9px;
    padding: 2px 6px;
}

/* ---- 事件切換下拉選單 ---- */

.event-select {
    font-family: inherit;
    font-size: 11px;
    line-height: 1.45;
    color: rgba(255, 255, 255, 0.88);
    /* 用 background-color 而非 background 簡寫，否則會蓋掉下面的箭頭圖 */
    background-color: rgba(0, 0, 0, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 999px;
    padding: 3px 24px 3px 10px;
    max-width: 100%;
    /* .overlay-top 是 flex column，子元素預設會被撐滿寬度；
       這裡要跟著文字內容縮寬，不能整條撐到影像邊緣 */
    align-self: flex-start;
    cursor: pointer;
    white-space: nowrap;
    backdrop-filter: blur(4px);
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23ffffff' stroke-opacity='.8' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 9px center;
    transition:
        background-color 0.2s,
        color 0.2s;
}

.event-select:hover {
    background-color: rgba(255, 255, 255, 0.22);
}

/* 正在看特定事件時轉琥珀色，一眼看得出不是「最近的流星」。
   這個顏色補上了原本 info-panel 裡那顆 ✦ 標籤的作用 */
.event-select.active {
    background-color: rgba(224, 162, 74, 0.92);
    border-color: transparent;
    color: #1a1206;
    font-weight: 600;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%231a1206' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
}

.event-select:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
}

/* OS 的選單配色不繼承頁面樣式，不明寫會有深色主題下白字白底的風險 */
.event-select option {
    color: #1a1206;
    background: #fff;
    font-weight: 400;
}

.event-select option:disabled {
    color: #999;
}

.nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: none;
    padding: 10px 15px;
    cursor: pointer;
    border-radius: 50%;
    transition: 0.3s;
}

.nav-btn:hover {
    background: rgba(255, 255, 255, 0.4);
}

.prev-btn {
    left: 10px;
}

.next-btn {
    right: 10px;
}

/* slide bar 取代原本的 dots */
.slide-bar-wrapper {
    position: absolute;
    bottom: 12px;
    left: 50%;
    transform: translateX(-50%);
    width: 70%;
    display: flex;
    align-items: center;
    gap: 10px;
}

.slide-bar-wrapper.compact {
    bottom: 8px;
    width: 80%;
    gap: 0;
}

.slide-bar {
    -webkit-appearance: none;
    appearance: none;
    flex: 1;
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.3);
    cursor: pointer;
    outline: none;
}

.slide-bar-wrapper.compact .slide-bar {
    pointer-events: none;
    height: 3px;
}

.slide-bar::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 13px;
    height: 13px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
    cursor: pointer;
}

.slide-bar::-moz-range-thumb {
    width: 13px;
    height: 13px;
    border: none;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
    cursor: pointer;
}

.slide-bar-wrapper.compact .slide-bar::-webkit-slider-thumb {
    width: 0;
    height: 0;
}

.slide-bar-wrapper.compact .slide-bar::-moz-range-thumb {
    width: 0;
    height: 0;
}

.counter {
    color: #fff;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
    min-width: 48px;
    text-align: right;
}
</style>
